import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Globe, ExternalLink, RefreshCw, MapPin, AlertTriangle, Clock, User as UserIcon,
  Calendar, CheckCircle2, Tag, FileText, Briefcase, Mail, Phone,
  Play, Square, ArrowRight, Pencil
} from "lucide-react";

// =============================================================================
// SurveydraftsJobPanel — read-only mirror of every SD-side detail on a job
// Used inside JobDetailModal for jobs where source === "surveydrafts".
// Single API call: GET /jobs/{id}/surveydrafts-full (cache-backed 5-min TTL).
// =============================================================================
export default function SurveydraftsJobPanel({ jobId }) {
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  const [tab, setTab] = useState("overview");

  const load = (refresh = false) => {
    setState((s) => ({ ...s, loading: true }));
    api.get(`/jobs/${jobId}/surveydrafts-full`, { params: refresh ? { refresh: true } : {} })
      .then(({ data }) => setState({ loading: false, data, err: data?.ok ? "" : (data?.reason || "Unable to load") }))
      .catch(() => setState({ loading: false, data: null, err: "Request failed" }));
  };
  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [jobId]);

  if (state.loading) {
    return (
      <div className="brut-card p-4 bg-emerald-50 border-emerald-300" data-testid="sd-panel">
        <div className="font-mono text-xs text-zinc-600">Loading surveydrafts.us details…</div>
      </div>
    );
  }
  if (!state.data?.ok) {
    return (
      <div className="brut-card p-4 bg-amber-50 border-amber-400" data-testid="sd-panel">
        <div className="flex items-center gap-2 text-xs font-mono text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>{state.err || "Could not load surveydrafts data"}</span>
          <button onClick={() => load(true)} className="ml-auto brut-btn-secondary px-2 py-0.5 text-[10px]">RETRY</button>
        </div>
      </div>
    );
  }

  const { sd_job, assignees, vendor, history, work_sessions, assignment_history, cached, age_min } = state.data;

  return (
    <div className="brut-card overflow-hidden bg-emerald-50 border-emerald-300" data-testid="sd-panel">
      <div className="flex items-center justify-between px-4 py-2 border-b-2 border-zinc-900 bg-emerald-100">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-emerald-700" />
          <div className="font-heading font-black text-sm">SURVEYDRAFTS.US DETAIL</div>
          <div className="label-mono text-[10px] text-zinc-500">
            · {cached ? `cached ${age_min ?? "?"}m ago` : "live"}
          </div>
        </div>
        <button onClick={() => load(true)} className="brut-btn-secondary px-2 py-1 text-[10px] inline-flex items-center gap-1" data-testid="sd-panel-refresh">
          <RefreshCw className="w-3 h-3" /> REFRESH
        </button>
      </div>

      <div className="flex gap-1 px-3 pt-3 border-b border-zinc-200" data-testid="sd-panel-tabs">
        {[
          { k: "overview", label: "OVERVIEW" },
          { k: "assignments", label: `ASSIGNMENTS (${assignment_history?.length || 0})` },
          { k: "sessions", label: `WORK SESSIONS (${work_sessions?.length || 0})` },
          { k: "history", label: `STATUS HISTORY (${history?.length || 0})` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 text-[10px] font-mono font-bold border border-zinc-900 border-b-0 ${tab === t.k ? "bg-yellow-200" : "bg-white hover:bg-zinc-50"}`}
            data-testid={`sd-panel-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {tab === "overview" && <Overview sd={sd_job} vendor={vendor} assignees={assignees} />}
        {tab === "assignments" && <AssignmentsTab rows={assignment_history} />}
        {tab === "sessions" && <SessionsTab rows={work_sessions} />}
        {tab === "history" && <HistoryTab rows={history} />}
      </div>
    </div>
  );
}

// ============================================================================
function Overview({ sd, vendor, assignees }) {
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-xs font-mono">
      {/* Left column — job meta */}
      <div className="space-y-2">
        <Row icon={Calendar} label="Created"      value={fmt(sd?.created_at)} />
        <Row icon={Play}     label="Started"      value={fmt(sd?.started_at)} />
        <Row icon={CheckCircle2} label="Completed" value={fmt(sd?.completed_at)} />
        <Row icon={Calendar} label="Due"          value={sd?.due_date || "—"} />
        <Row icon={Globe}    label="SD Status"    value={sd?.status_label || sd?.status || "—"} />
        <Row icon={Briefcase} label="Vendor"      value={vendor?.name || "—"} />
        {sd?.important_instructions && (
          <div className="border border-zinc-900 bg-yellow-100 p-2 mt-2" data-testid="sd-important">
            <div className="label-mono text-zinc-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> IMPORTANT INSTRUCTIONS</div>
            <div className="text-xs mt-1 whitespace-pre-wrap">{sd.important_instructions}</div>
          </div>
        )}
        {sd?.notes && (
          <div className="border border-zinc-200 bg-white p-2 mt-2">
            <div className="label-mono text-zinc-500"><Pencil className="w-3 h-3 inline mr-1" /> SD NOTES</div>
            <div className="text-xs mt-1 whitespace-pre-wrap">{sd.notes}</div>
          </div>
        )}
        {Array.isArray(sd?.tags) && sd.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2" data-testid="sd-tags">
            {sd.tags.map((t) => (
              <span key={t} className="px-1.5 py-0.5 border border-zinc-900 bg-pink-100 text-[10px] inline-flex items-center gap-1">
                <Tag className="w-2.5 h-2.5" /> {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Right column — assignees + location */}
      <div className="space-y-2">
        <div>
          <div className="label-mono mb-1">ASSIGNEES</div>
          <AssigneeRow role="Drafter"     m={assignees?.drafter} />
          <AssigneeRow role="QA / QC"     m={assignees?.qaqc} />
          <AssigneeRow role="Final QC"    m={assignees?.finalqc} />
          <AssigneeRow role="Field Crew"  m={assignees?.fieldcrew} />
          <AssigneeRow role="Created By"  m={assignees?.created_by} />
        </div>
        <div className="pt-2">
          <div className="label-mono mb-1">LOCATION</div>
          <div className="flex items-start gap-1.5 text-xs">
            <MapPin className="w-3 h-3 mt-0.5 text-zinc-500 shrink-0" />
            <span className="truncate" title={sd?.address}>{sd?.address || "—"}</span>
          </div>
          {(sd?.latitude && sd?.longitude) && (
            <div className="text-[10px] text-zinc-500 ml-4 mt-0.5">
              {sd.latitude.toFixed(5)}, {sd.longitude.toFixed(5)}
            </div>
          )}
          {sd?.google_maps_link && (
            <a
              href={sd.google_maps_link}
              target="_blank"
              rel="noreferrer noopener"
              className="brut-btn-secondary inline-flex items-center gap-1 px-2 py-1 text-[10px] mt-2"
              data-testid="sd-maps-link"
            >
              <ExternalLink className="w-3 h-3" /> OPEN IN GOOGLE MAPS
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className="w-3 h-3 text-zinc-500 mt-0.5 shrink-0" />
      <div className="label-mono w-24 shrink-0">{label}</div>
      <div className="flex-1 truncate" title={String(value || "")}>{value || "—"}</div>
    </div>
  );
}

function AssigneeRow({ role, m }) {
  if (!m) {
    return (
      <div className="text-xs flex items-center gap-2 py-0.5">
        <span className="label-mono w-20 text-zinc-500">{role}</span>
        <span className="text-zinc-400">— unassigned —</span>
      </div>
    );
  }
  return (
    <div className="text-xs flex items-center gap-2 py-0.5">
      <span className="label-mono w-20 text-zinc-500">{role}</span>
      <span className="font-bold">{m.name}</span>
      {m.email && (
        <a href={`mailto:${m.email}`} className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5">
          <Mail className="w-2.5 h-2.5" /> {m.email.slice(0, 22)}{m.email.length > 22 ? "…" : ""}
        </a>
      )}
      {m.phone && (
        <span className="text-[10px] text-zinc-500 inline-flex items-center gap-0.5">
          <Phone className="w-2.5 h-2.5" /> {m.phone}
        </span>
      )}
    </div>
  );
}

// ============================================================================
function AssignmentsTab({ rows }) {
  if (!rows || rows.length === 0)
    return <div className="text-center text-xs font-mono text-zinc-500 py-6">No assignment events recorded.</div>;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—";
  return (
    <ul className="divide-y divide-zinc-200">
      {rows.map((a, i) => (
        <li key={a.id || i} className="py-2 flex items-start gap-3 text-xs" data-testid={`sd-assign-${i}`}>
          <UserIcon className="w-3.5 h-3.5 text-zinc-500 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div>
              <span className="label-mono">{(a.assignment_type || "ASSIGN").toUpperCase()}</span>
              <span className="ml-2 font-bold">{a.assigned_to_name || "—"}</span>
              {a.assigned_by_name && <span className="text-zinc-500"> · by {a.assigned_by_name}</span>}
            </div>
            {a.comment && <div className="text-zinc-600 mt-0.5 truncate" title={a.comment}>{a.comment}</div>}
            <div className="text-[10px] text-zinc-500 mt-0.5">{fmt(a.assigned_at || a.created_at)}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ============================================================================
function SessionsTab({ rows }) {
  if (!rows || rows.length === 0)
    return <div className="text-center text-xs font-mono text-zinc-500 py-6">No work sessions recorded.</div>;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—";
  const totalHrs = rows.reduce((a, r) => a + (r.duration_hours || 0), 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono text-zinc-500">{rows.length} session{rows.length !== 1 ? "s" : ""} · total</div>
        <div className="font-heading text-base font-black">{totalHrs.toFixed(2)} hrs</div>
      </div>
      <table className="w-full text-xs font-mono" data-testid="sd-sessions-table">
        <thead className="bg-zinc-100 border-b-2 border-zinc-900">
          <tr>
            <th className="px-2 py-1 text-left">USER</th>
            <th className="px-2 py-1 text-left">ROLE</th>
            <th className="px-2 py-1 text-left">START</th>
            <th className="px-2 py-1 text-left">STOP</th>
            <th className="px-2 py-1 text-right">DURATION</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-zinc-100" data-testid={`sd-session-${r.id}`}>
              <td className="px-2 py-1">{r.user_name || "—"}</td>
              <td className="px-2 py-1"><span className="px-1 py-0.5 border border-zinc-300 bg-zinc-50 text-[9px]">{(r.role || "").toUpperCase()}</span></td>
              <td className="px-2 py-1">{fmt(r.start_time)}</td>
              <td className="px-2 py-1">{fmt(r.end_time || r.stop_time)}</td>
              <td className="px-2 py-1 text-right font-bold">{(r.duration_hours ?? 0).toFixed(2)}h</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
function HistoryTab({ rows }) {
  if (!rows || rows.length === 0)
    return <div className="text-center text-xs font-mono text-zinc-500 py-6">No status events.</div>;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—";
  const iconFor = (t) => ({
    timer_start: Play, timer_stop: Square,
    status_change: ArrowRight, completed: CheckCircle2,
    assignment: UserIcon, assignment_change: UserIcon,
    document_upload: FileText, note_added: Pencil,
  })[t] || Clock;
  return (
    <ul className="divide-y divide-zinc-200">
      {rows.map((e, i) => {
        const Icon = iconFor(e.action_type);
        return (
          <li key={e.id || i} className="py-2 flex items-start gap-2 text-xs" data-testid={`sd-history-${i}`}>
            <Icon className="w-3.5 h-3.5 text-zinc-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="truncate" title={e.description}>{e.description || "—"}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {e.performed_by_name || e.changed_by_name || "—"} · {fmt(e.created_at || e.changed_at)}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
